import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OutlinedSelectOption } from '@core/components/fc-select/base/fc-outlined-select/fc-outlined-select.component';
import { DeadlinesFilter, DeadlinesFilterForm, DeadlinesWhere } from '@core/interfaces';
import { endOfDay, mapId, mapIds, startOfDay } from '@core/utils/filters-utils';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DateValidation } from '@validators/date.validation';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import english from '../i18n/en.json';
import spanish from '../i18n/es.json';
import portuguese from '../i18n/pt.json';

@Component({
  selector: 'deadlines-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss']
})
export class FilterComponent implements OnInit {
  public isFilterExpanded: boolean;
  public filterForm: FormGroup;
  public readonly filterTypeOptions: OutlinedSelectOption[];

  private readonly defaultFilterValues = {
    dateFilterType: 'openedAt',
    initialDate: moment().subtract(1, 'month'),
    finalDate: moment(),
    locations: null,
    segments: null,
    companies: null,
    locationGroups: null,
    maintenanceTypes: null
  };

  public filtersChanged: BehaviorSubject<DeadlinesFilter> = new BehaviorSubject(
    this.toFilter(true)
  );

  constructor(
    private formBuilder: FormBuilder,
    private readonly translateService: TranslateService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService
    ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.filterTypeOptions = [
      {
        text: this.translateService.instant('DEADLINES.FILTERS.FILTER_BY.OPENED_AT'),
        value: 'openedAt',
      },
      {
        text: this.translateService.instant('DEADLINES.FILTERS.FILTER_BY.SCHEDULED_AT'),
        value: 'scheduledAt',
      },
      {
        text: this.translateService.instant('DEADLINES.FILTERS.FILTER_BY.CLOSED_AT'),
        value: 'closedAt',
      }
    ];
  }

  public ngOnInit(): void {
    this.isFilterExpanded = false;

    this.filterForm = this.formBuilder.group({
      dateFilterType: [this.defaultFilterValues.dateFilterType, [Validators.required]],
      initialDate: [this.defaultFilterValues.initialDate, [Validators.required, DateValidation.validate]],
      finalDate: [this.defaultFilterValues.finalDate, [Validators.required, DateValidation.validate]],
      locations: this.defaultFilterValues.locations,
      segments: this.defaultFilterValues.segments,
      companies: this.defaultFilterValues.companies,
      locationGroups: this.defaultFilterValues.locationGroups,
      maintenanceTypes: this.defaultFilterValues.maintenanceTypes
    }, {
      validators: [
        DateIntervalValidation.validate({
          from: 'initialDate',
          to: 'finalDate',
          maxDiffLte: 1,
          unit: 'year'
        })
      ]
    });

    this.filterForm.valueChanges
      .pipe(filter(() => this.filterForm.valid))
      .subscribe(() => this.updateFilter());
  }

  @HostListener('window:beforeprint')
  public async beforePrint(): Promise<void> {
    this.isFilterExpanded = true;
  }

  private toQuery({ isDefault, isFooter }): DeadlinesWhere {
    const value = isDefault ? this.defaultFilterValues : this.filterForm.value as DeadlinesFilterForm;

    const initialDate = moment(value.initialDate).startOf('day');
    const finalDate = moment(value.finalDate).endOf('day');

    const interval = finalDate.diff(initialDate, 'days');
    const previousInitialDate = moment(initialDate).subtract(interval, 'days');
    const previousFinalDate = moment(initialDate).subtract(1, 'days');

    const where = {
      locationId_in: mapIds(value.locations),
      segmentId_in: mapIds(value.segments),
      companyId_in: mapIds(value.companies),
      maintenanceTypeId_in: mapIds(value.maintenanceTypes),
      locationGroupId_in: mapIds(value.locationGroups)
    };
    where[`${value.dateFilterType}_gte`] = startOfDay(isFooter ? previousInitialDate : initialDate);
    where[`${value.dateFilterType}_lte`] = endOfDay(isFooter ? previousFinalDate : finalDate);

    return where;
  }

  private toFilter(isDefault: boolean): DeadlinesFilter {
    return {
      currentWhere: this.toQuery({ isDefault, isFooter: false }),
      footerWhere: this.toQuery({ isDefault, isFooter: true }),
      isFiltered: isDefault ? false : JSON.stringify(this.defaultFilterValues) !== JSON.stringify(this.filterForm.value)
    };
  }

  private updateFilter(): void {
    const params = this.toFilter(false);
    this.filtersChanged.next(params);
  }

  public clearFilters(): void {
    this.filterForm.patchValue(this.defaultFilterValues);
  }
}
